// @flow strict

import * as React from 'react';
import throttle from '@ta/common.throttle';

/** The component props for a <WindowScroll> */
export type Options = {|
  /** Optional: The minimum number of milliseconds between invocations of the callback */
  throttle?: number,

  /** Optional: Whether to use throttle like debounce */
  useDebounce?: boolean,

  /** Optional: Change the way to attach handler to scroll and resize events: event capturing or event bubbling */
  useCapture?: boolean,
|};

/**
 * Event handler component that listens for 'scroll' and 'resize' (which can imply scroll) events. You provide a
 * callback along with a throttle to limit the rate of callbacks
 */
const useWindowScroll = (callback: () => mixed, opts?: Options) => {
  const throttleVal = opts?.throttle || 200;
  const useDebounce = !!opts?.useDebounce;
  const useCapture = !!opts?.useCapture;
  const handleScroll = React.useCallback(throttle(callback, throttleVal, useDebounce), [
    callback,
    throttleVal,
    useDebounce,
  ]);

  React.useEffect(() => {
    /* eslint-disable no-restricted-globals */
    window.addEventListener('scroll', handleScroll, useCapture);
    window.addEventListener('resize', handleScroll, useCapture);

    return () => {
      window.removeEventListener('scroll', handleScroll, useCapture);
      window.removeEventListener('resize', handleScroll, useCapture);
    };
    /* eslint-enable no-restricted-globals */
  }, [handleScroll, useCapture]);

  return null;
};

export default useWindowScroll;
